import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography } from '@mui/material';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import PointOfSaleRoundedIcon from '@mui/icons-material/PointOfSaleRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import HandshakeRoundedIcon from '@mui/icons-material/HandshakeRounded';
import MoveToInboxRoundedIcon from '@mui/icons-material/MoveToInboxRounded';
import ArrowCircleDownRoundedIcon from '@mui/icons-material/ArrowCircleDownRounded';
import ArrowCircleUpRoundedIcon from '@mui/icons-material/ArrowCircleUpRounded';
import SyncAltRoundedIcon from '@mui/icons-material/SyncAltRounded';
import RequestQuoteRoundedIcon from '@mui/icons-material/RequestQuoteRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import BuildRoundedIcon from '@mui/icons-material/BuildRounded';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import StackedLineChartRoundedIcon from '@mui/icons-material/StackedLineChartRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import PeopleRoundedIcon from '@mui/icons-material/PeopleRounded';

import { AppCard, IconBadge } from '../../shared/components/ui';
import { IconBadgeTone } from '../../shared/themes';
import { useModulosEmpresa } from '../../shared/contexts';
import { obterUltimosAcessos, rotaBase } from '../../shared/services/util/registroDeAcessos';
import { SecaoGrade } from './SecaoGrade';

interface IAtalho {
  label: string;
  rota: string;
  icone: React.ReactElement;
  tom: IconBadgeTone;
}

const ATALHOS: IAtalho[] = [
  { label: 'Vendas', rota: '/venda', icone: <PointOfSaleRoundedIcon />, tom: 'primary' },
  { label: 'Produtos', rota: '/produto', icone: <Inventory2RoundedIcon />, tom: 'cyan' },
  { label: 'Parceiros', rota: '/parceiros', icone: <HandshakeRoundedIcon />, tom: 'purple' },
  { label: 'Entrada', rota: '/entrada', icone: <MoveToInboxRoundedIcon />, tom: 'cyan' },
  { label: 'Contas a receber', rota: '/contasreceber', icone: <ArrowCircleDownRoundedIcon />, tom: 'success' },
  { label: 'Contas a pagar', rota: '/contaspagar', icone: <ArrowCircleUpRoundedIcon />, tom: 'error' },
  { label: 'Conciliação', rota: '/conciliacao', icone: <SyncAltRoundedIcon />, tom: 'neutral' },
  { label: 'Faturamento', rota: '/faturamento', icone: <RequestQuoteRoundedIcon />, tom: 'success' },
  { label: 'NF-e', rota: '/nfe', icone: <DescriptionRoundedIcon />, tom: 'warning' },
  { label: 'NFC-e', rota: '/nfce', icone: <ReceiptLongRoundedIcon />, tom: 'warning' },
  { label: 'Ordens de serviço', rota: '/ordensservico', icone: <BuildRoundedIcon />, tom: 'neutral' },
  { label: 'Transportadoras', rota: '/transportadoras', icone: <LocalShippingRoundedIcon />, tom: 'purple' },
  { label: 'Estoque mínimo', rota: '/estoque/estoqueminimo', icone: <WarningAmberRoundedIcon />, tom: 'warning' },
  { label: 'Gráficos de vendas', rota: '/dashboardevolucaovendas', icone: <InsightsRoundedIcon />, tom: 'primary' },
  { label: 'Gráficos financeiros', rota: '/dashboardcontas', icone: <StackedLineChartRoundedIcon />, tom: 'success' },
  { label: 'Usuários', rota: '/usuarios', icone: <PeopleRoundedIcon />, tom: 'neutral' },
];

export const AcessosRapidos: React.FC = () => {
  const navigate = useNavigate();
  const { rotaLiberada } = useModulosEmpresa();

  // Telas abertas mais recentemente primeiro (ver registrarAcesso em
  // routes/index.tsx); as nunca abertas ficam no fim, na ordem padrão. A
  // ordem é calculada ao abrir o painel — cada volta pro Início já reflete
  // as telas visitadas desde a última vez.
  const [atalhos] = useState(() => {
    const ultimosAcessos = obterUltimosAcessos();
    const momento = (atalho: IAtalho) => ultimosAcessos[rotaBase(atalho.rota)] ?? 0;
    return [...ATALHOS].sort((a, b) => momento(b) - momento(a));
  });

  return (
    <SecaoGrade titulo="Acessos rápidos" icone={<BoltRoundedIcon fontSize="small" />} larguraMinimaCard={112}>
      {atalhos.filter(atalho => rotaLiberada(atalho.rota)).map(atalho => (
        <AppCard
          key={atalho.rota}
          onClick={() => navigate(atalho.rota)}
          sx={{
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            textAlign: 'center',
          }}
        >
          <IconBadge icon={atalho.icone} tone={atalho.tom} size={40} />
          <Typography variant="caption" fontWeight={600} lineHeight={1.25}>{atalho.label}</Typography>
        </AppCard>
      ))}
    </SecaoGrade>
  );
};
